import React, { createContext, useContext, useMemo, useState } from 'react';
import { fieldInvalidText } from '../workflow/constants';

// Submit-time required-field marking — the single source (docs/workflow-ui.md §4.2).
//
// A long form's "Please fill in: a, b, c" banner names the gaps but not where
// they are, and the requester then hunts through seven cards. So the page runs
// its required check, every FormKit `Field` whose `field` key is in the gap
// list marks ITSELF (red label, red halo, "Required" caption), and the page
// scrolls to the first mark. Nothing is marked before the first Submit attempt
// — a form that is red on arrival reads as broken, not as a to-do. From then
// on the marks are live: fill a field and its mark goes away.
//
// Keys are the same `field` keys the return-comment layer uses (a Field carries
// one `field=` prop for both), so a gap list is `[{ key, label }]`. Controls
// that are not a Field (a checkbox list, a declaration line) drop a
// `<FieldInvalidInline field=…>` where the caption belongs and read
// `useFieldInvalid(key)` for their own styling.
//
// Outside a provider every hook returns null and every component renders
// nothing, so forms that have not opted in are unaffected.

const FormValidationContext = createContext(null);
export const FormValidationProvider = FormValidationContext.Provider;

// Set on every marked element so the page can scroll to the first one.
export const INVALID_FIELD_ATTR = 'data-invalid-field';

/** The gap entry for this key, or null (not attempted / filled / no provider). */
export const useFieldInvalid = (field) => {
  const ctx = useContext(FormValidationContext);
  if (!ctx || !field) return null;
  return ctx.byKey[field] || null;
};

/** The red caption under a control that is not a FormKit Field. */
export const FieldInvalidInline = ({ field, text = 'Required' }) => {
  const gap = useFieldInvalid(field);
  if (!gap) return null;
  return <p className={fieldInvalidText} {...{ [INVALID_FIELD_ATTR]: field }}>{text}</p>;
};

/** Scroll the first marked control into view — after the marks have rendered. */
export const scrollToFirstInvalid = () => {
  setTimeout(() => {
    const el = document.querySelector(`[${INVALID_FIELD_ATTR}]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 0);
};

// A QC acceptance form can be missing fifty answers at once; naming every one
// in the banner turns it into a wall of text nobody reads, and the marks on
// the page already say where each gap is. So the banner names the first few
// and counts the rest.
const SUMMARY_NAMED = 12;

/** Banner text — the count first, so the requester knows how many marks to find. */
export const requiredSummary = (gaps) => {
  if (!gaps || gaps.length === 0) return null;
  const n = gaps.length;
  const named = gaps.slice(0, SUMMARY_NAMED).map((g) => g.label).join(', ');
  const rest = n - SUMMARY_NAMED;
  return `Please fill in the ${n} required field${n === 1 ? '' : 's'} marked in red: ${named}${rest > 0 ? ` … and ${rest} more` : ''}`;
};

/**
 * Page-side state machine.
 * @param gaps the CURRENT gap list `[{ key, label }]` — memoised by the page
 * @returns validate()     call first thing in submit; marks the form and returns
 *                         false while gaps remain
 *          error          banner text while attempted && gaps remain, else null
 *          providerValue  hand to <FormValidationProvider value=…>
 */
export const useFormValidation = (gaps) => {
  const [attempted, setAttempted] = useState(false);
  const shown = attempted ? gaps : null;
  const providerValue = useMemo(
    () => ({ byKey: Object.fromEntries((shown || []).map((g) => [g.key, g])) }),
    [shown]
  );
  const validate = () => {
    setAttempted(true);
    if (gaps.length > 0) { scrollToFirstInvalid(); return false; }
    return true;
  };
  return { validate, error: requiredSummary(shown), providerValue, attempted };
};
